import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { getAuthToken, getUserEmail } from '@public/store/selectors/auth.selector';
import { Message } from '@shared/models/shared.model';
import {
  GenerateOtpParams,
  OtpParams,
  OtpResponse,
  PasswordResetParams,
  ResendLinkResponse,
  ResendPassCodeParams,
  ResetPasswordParams,
  SignInParams,
  SignInResponse,
} from '@views/public/store/models/auth.model';
import { StoreState } from 'app/store/store';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  url = environment.baseUrl;

  constructor(
    public store: Store<StoreState['auth']>,
    private http: HttpClient
  ) {}

  authToken(): string {
    let authenticationToken: string;

    this.store
      .pipe(select(getAuthToken))
      .pipe(take(1))
      .subscribe((token) => {
        authenticationToken = token;
      });

    return authenticationToken;
  }

  email(): string {
    let currentUserEmail: string;

    this.store
      .pipe(select(getUserEmail))
      .pipe(take(1))
      .subscribe((email) => {
        currentUserEmail = email;
      });

    return currentUserEmail;
  }

  signIn(params: SignInParams): Observable<HttpResponse<SignInResponse>> {
    return this.http.post<SignInResponse>(`${this.url}/v1/signin`, params, {
      observe: 'response',
    });
  }

  verifyOtp(params: OtpParams, mfaToken: string): Observable<HttpResponse<OtpResponse>> {
    const headers = new HttpHeaders({
      Authorization: mfaToken,
    });
    return this.http.post<OtpResponse>(`${this.url}/v1/verify_mfa`, params, {
      headers,
      observe: 'response',
    });
  }

  resetPasswordLink(params: PasswordResetParams): Observable<ResendLinkResponse> {
    return this.http.put<ResendLinkResponse>(`${this.url}/v1/password/reset_link/send`, params);
  }

  validateUserToken(token: string): Observable<Message> {
    const headers = new HttpHeaders({
      Authorization: token,
    });
    return this.http.get<Message>(`${this.url}/v1/password/reset/validate`, {
      headers,
    });
  }

  resetPassword(params: ResetPasswordParams): Observable<ResendLinkResponse> {
    return this.http.put<ResendLinkResponse>(`${this.url}/v1/password/reset`, params);
  }

  resendOtp(params: ResendPassCodeParams, mfaToken: string): Observable<SignInResponse> {
    const headers = new HttpHeaders({
      Authorization: mfaToken,
    });
    return this.http.post<SignInResponse>(`${this.url}/v1/resent_otp`, params, {
      headers,
    });
  }

  signOut() {
    return this.http.delete(`${this.url}/v1/signout`);
  }

  refreshToken(): Observable<HttpResponse<string>> {
    return this.http.post<string>(`${this.url}/v1/refresh_token`, null, {
      observe: 'response',
    });
  }

  switchOrganization(organizationId: number): Observable<HttpResponse<OtpResponse>> {
    return this.http.post<OtpResponse>(
      `${this.url}/v1/switch_organization`,
      { organization_id: organizationId },
      {
        observe: 'response',
      }
    );
  }

  generateEmailViaVerificationOtp(
    params: GenerateOtpParams,
    mfaToken: string
  ): Observable<Message> {
    const headers = new HttpHeaders({
      Authorization: mfaToken,
    });
    return this.http.post<Message>(`${this.url}/v1/generate_otp`, params, { headers });
  }
}
